import { FC, useState } from 'react';

import { Box } from '@mui/material';

import { PublicSnapTemplate } from '@sparkplug/lib';

import { useSnapTemplateQuery } from '@features/spark-snaps/queries';

import { Snaps } from '@components/icons';
import Spinner from '@components/layout/Spinner';

import SnapPosterThumbnail, { SnapPosterThumbnailProps } from '../SnapPosterThumbnail';

/**
 * Creates a consistent look for the fallback elements.
 */
const FallbackContainer: FC = ({ children }) => (
    <Box
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ backgroundColor: 'white' }}
    >
        {children}
    </Box>
);

interface TemplateThumbnailProps {
    accountId: string;
    snapTemplate: PublicSnapTemplate;
    dropdownMenu: SnapPosterThumbnailProps['dropdownMenu'];
}
export const TemplateThumbnail: FC<TemplateThumbnailProps> = ({
    accountId,
    snapTemplate,
    dropdownMenu,
}) => {
    const [previewImgIsBroken, setPreviewImgIsBroken] = useState<boolean>(false);

    const { snapTemplate: snapTemplateData = snapTemplate, snapTemplateIsLoading } =
        useSnapTemplateQuery({
            accountId,
            snapTemplateId: snapTemplate.id,
            /**
             * When the image is broken refetch this snapTemplate.
             * When fetching a single snapTemplate we wait for up to a fixed amount of time for the third party image CDN to propogate the previewImg.
             */
            enabled: previewImgIsBroken,
        });

    let previewElement = (
        <img
            src={snapTemplateData.previewImg}
            alt={snapTemplateData.name}
            onError={() => setPreviewImgIsBroken(true)}
        />
    );
    if (snapTemplateIsLoading) {
        previewElement = (
            <FallbackContainer>
                <Spinner />
            </FallbackContainer>
        );
    } else if (snapTemplate.previewImgStatus === 'failed') {
        previewElement = (
            <FallbackContainer>
                <div className="img-broken">
                    <Snaps />
                </div>
            </FallbackContainer>
        );
    }

    return (
        <SnapPosterThumbnail
            imgElement={previewElement}
            title={snapTemplate.name}
            slideCount={snapTemplate.slideCount || 0}
            dropdownMenu={dropdownMenu}
        />
    );
};
